import React, { useState, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
// import { EyeIcon } from '@heroicons/react/24/outline'; // Unused for now
import { tradingPairsApi, exchangeApi } from '@/services/api';
import { TradingPair, ExchangeBalance } from '@/types';
// import { formatCurrency } from '@/utils/formatters'; // Unused for now
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import Modal from '@/components/UI/Modal';
import TradingPairForm from '@/components/Forms/TradingPairForm';
import ConfirmDialog from '@/components/UI/ConfirmDialog';
import toast from 'react-hot-toast';

interface TradingPairsPageState {
  pairs: TradingPair[];
  balances: ExchangeBalance[];
  loading: boolean;
  error: string | null;
  selectedPair: TradingPair | null;
  showForm: boolean;
  showDeleteConfirm: boolean;
  formMode: 'create' | 'edit';
  filters: {
    search: string;
    exchange: string;
    status: string; // all, active, inactive
  };
}

const TradingPairsPage: React.FC = () => {
  const [state, setState] = useState<TradingPairsPageState>({
    pairs: [],
    balances: [],
    loading: true,
    error: null,
    selectedPair: null,
    showForm: false,
    showDeleteConfirm: false,
    formMode: 'create',
    filters: {
      search: '',
      exchange: '',
      status: 'all', // all, active, inactive
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      console.log('[TRADING_PAIRS] Starting parallel API loading at:', new Date().toISOString());
      const startTime = Date.now();

      // PERFORMANCE OPTIMIZATION: Execute all APIs in parallel instead of sequential
      const [apiKeysRes, pairsRes, balancesRes] = await Promise.allSettled([
        exchangeApi.getApiKeys(),
        tradingPairsApi.getAll({ includeDetails: true }),
        exchangeApi.getAggregatedBalance({ includeZero: true }) // Use optimized aggregated endpoint
      ]);

      const loadTime = Date.now() - startTime;
      console.log(`[TRADING_PAIRS] Parallel API loading completed in ${loadTime}ms`);

      // Process API keys response
      let availableExchanges: any[] = [];
      if (apiKeysRes.status === 'fulfilled') {
        // Backend now returns { data: [...], pagination: {...} } structure
        availableExchanges = (apiKeysRes.value as any).data?.data?.filter((key: any) => key.isActive) || [];
        console.log(`[TRADING_PAIRS] Loaded ${availableExchanges.length} active exchanges`);
      } else {
        console.warn('[TRADING_PAIRS] Could not fetch API keys:', apiKeysRes.reason);
      }
      
      // Process trading pairs response
      let transformedPairs: any[] = [];
      if (pairsRes.status === 'fulfilled') {
        // Transform backend response: snake_case → camelCase + string → number
        transformedPairs = ((pairsRes.value as any).data?.tradingPairs || []).map((pair: any) => ({
          ...pair,
          targetAllocation: parseFloat(pair.target_allocation || '0'),
          currentAllocation: parseFloat(pair.current_allocation || '0'),
          isActive: pair.is_active,
          exchangeMarketId: pair.exchange_market_id, // Include new exchange market ID field
          // Keep snake_case properties for backward compatibility
          target_allocation: pair.target_allocation,
          current_allocation: pair.current_allocation
        }));
        
        console.log(`[TRADING_PAIRS] Transformed ${transformedPairs.length} pairs:`, 
          transformedPairs.map((p: any) => `${p.symbol}: target=${p.targetAllocation}%, current=${p.currentAllocation}%`));
      } else {
        console.warn('[TRADING_PAIRS] Could not fetch trading pairs:', pairsRes.reason);
      }
      
      // Process aggregated balances response (OPTIMIZED)
      let balances: any[] = [];
      if (balancesRes.status === 'fulfilled') {
        const aggregatedData = (balancesRes.value as any).aggregated || {};
        // Convert aggregated balance format to compatible format for existing UI
        balances = Object.values(aggregatedData).map((currencyData: any) => ({
          currency: currencyData.currency,
          total: currencyData.totalBalance,
          available: currencyData.totalBalance, // Simplified for trading pairs view
          exchanges: currencyData.exchanges
        }));
        console.log(`[TRADING_PAIRS] Loaded aggregated balances for ${balances.length} currencies`);
      } else {
        console.warn('[TRADING_PAIRS] Could not fetch aggregated balance:', balancesRes.reason);
      }

      setState(prev => ({
        ...prev,
        pairs: transformedPairs,
        balances: balances,
        loading: false,
      }));

      console.log(`[TRADING_PAIRS] Page fully loaded in ${loadTime}ms with parallel API execution`);
    } catch (error) {
      console.error('[TRADING_PAIRS] Error fetching trading pairs:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load trading pairs. Please try again.',
      }));
    }
  };

  const handleCreatePair = () => {
    setState(prev => ({
      ...prev,
      selectedPair: null,
      formMode: 'create',
      showForm: true,
    }));
  };

  const handleEditPair = (pair: TradingPair) => {
    setState(prev => ({
      ...prev,
      selectedPair: pair,
      formMode: 'edit',
      showForm: true,
    }));
  };

  const handleDeletePair = (pair: TradingPair) => {
    setState(prev => ({
      ...prev,
      selectedPair: pair,
      showDeleteConfirm: true,
    }));
  };

  const handleFormSubmit = async (data: Partial<TradingPair>) => {
    try {
      if (state.formMode === 'create') {
        await tradingPairsApi.create(data);
        toast.success('Trading pair created successfully');
      } else if (state.selectedPair) {
        await tradingPairsApi.update(state.selectedPair.id, data);
        toast.success('Trading pair updated successfully');
      }

      setState(prev => ({ ...prev, showForm: false, selectedPair: null }));
      fetchData();
    } catch (error) {
      console.error('Error saving trading pair:', error);
      toast.error('Failed to save trading pair');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!state.selectedPair) return;

    try {
      await tradingPairsApi.delete(state.selectedPair.id);
      toast.success('Trading pair deleted successfully');
      setState(prev => ({ ...prev, showDeleteConfirm: false, selectedPair: null }));
      fetchData();
    } catch (error) {
      console.error('Error deleting trading pair:', error);
      toast.error('Failed to delete trading pair');
    }
  };

  const filteredPairs = state.pairs.filter(pair => {
    const matchesSearch = pair.symbol.toLowerCase().includes(state.filters.search.toLowerCase()) ||
                         pair.baseCurrency.toLowerCase().includes(state.filters.search.toLowerCase()) ||
                         pair.quoteCurrency.toLowerCase().includes(state.filters.search.toLowerCase());
    
    const matchesExchange = !state.filters.exchange || pair.exchangeName === state.filters.exchange;
    
    const matchesStatus = state.filters.status === 'all' ||
                         (state.filters.status === 'active' && pair.isActive) ||
                         (state.filters.status === 'inactive' && !pair.isActive);

    return matchesSearch && matchesExchange && matchesStatus;
  });

  const totalAllocation = state.pairs.reduce((sum, pair) => sum + pair.targetAllocation, 0);
  const activeExchanges = [...new Set(state.pairs.map(pair => pair.exchangeName))];
  const activePairs = state.pairs.filter(p => p.isActive).length;

  if (state.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-heading-1 font-bold text-primary-custom">Trading Pairs</h1>
          <p className="text-body text-secondary-custom mt-1">
            Manage your trading pairs and portfolio allocations
          </p>
        </div>
        <button
          onClick={handleCreatePair}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Trading Pair
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <h3 className="text-caption text-secondary-custom mb-2">Total Pairs</h3>
          <p className="text-data-large text-primary-custom">{state.pairs.length}</p>
        </div>
        <div className="card">
          <h3 className="text-caption text-secondary-custom mb-2">Active Pairs</h3>
          <p className="text-data-large text-primary-custom">{activePairs}</p>
        </div>
        <div className="card">
          <h3 className="text-caption text-secondary-custom mb-2">Total Allocation</h3>
          <p className={`text-data-large ${totalAllocation > 100 ? 'text-error-custom' : 'text-primary-custom'}`}>
            {totalAllocation.toFixed(1)}%
          </p>
        </div>
        <div className="card">
          <h3 className="text-caption text-secondary-custom mb-2">Exchanges</h3>
          <p className="text-data-large text-primary-custom">{activeExchanges.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label">
              Search Pairs
            </label>
            <input
              type="text"
              placeholder="Enter symbol or exchange..."
              value={state.filters.search}
              onChange={(e) => setState(prev => ({ ...prev, filters: { ...prev.filters, search: e.target.value } }))}
              className="form-input form-bg-custom form-border-custom form-text-custom form-placeholder-custom focus-border-custom focus-ring-custom"
            />
          </div>
          <div>
            <label className="form-label">
              Filter by Exchange
            </label>
            <select
              value={state.filters.exchange}
              onChange={(e) => setState(prev => ({ ...prev, filters: { ...prev.filters, exchange: e.target.value } }))}
              className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom"
            >
              <option value="">All Exchanges</option>
              {activeExchanges.map(exchange => (
                <option key={exchange} value={exchange}>{exchange}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">
              Status Filter
            </label>
            <select
              value={state.filters.status}
              onChange={(e) => setState(prev => ({ ...prev, filters: { ...prev.filters, status: e.target.value } }))}
              className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Trading Pairs Table */}
      <div className="card">
        <div className="border-b border-primary-custom pb-4 mb-6">
          <h3 className="text-heading-3 text-primary-custom">
            Trading Pairs ({filteredPairs.length})
          </h3>
        </div>

        {state.loading ? (
          <div className="text-secondary-custom mb-4">
            Loading trading pairs...
          </div>
        ) : filteredPairs.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-secondary-custom">No trading pairs found matching your criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-primary-custom">
              <thead className="bg-secondary-custom">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-custom uppercase tracking-wider">
                    Trading Pair
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-custom uppercase tracking-wider">
                    Exchange
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-custom uppercase tracking-wider">
                    Target Allocation
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-custom uppercase tracking-wider">
                    Current Allocation
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-custom uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-custom uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-primary-custom">
                {filteredPairs.map((pair) => (
                  <tr key={pair.id} className="hover-bg-secondary-custom transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-body font-medium text-primary-custom">
                          {pair.symbol}
                        </div>
                        <div className="text-caption text-secondary-custom ml-2">
                          {pair.baseCurrency}/{pair.quoteCurrency}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-body text-primary-custom">
                      {pair.exchangeName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-body text-primary-custom">
                      {pair.targetAllocation.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-body text-primary-custom">
                      {pair.currentAllocation.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`status-indicator ${pair.isActive ? 'status-success' : 'status-error'}`}>
                        {pair.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-body font-medium">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleEditPair(pair)}
                          className="text-info-custom hover-text-primary-custom"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeletePair(pair)}
                          className="text-error-custom hover-text-primary-custom"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <Modal
        isOpen={state.showForm}
        onClose={() => setState(prev => ({ ...prev, showForm: false, selectedPair: null }))}
        title={state.formMode === 'create' ? 'Add Trading Pair' : 'Edit Trading Pair'}
        size="lg"
      >
        <TradingPairForm
          initialData={state.selectedPair}
          onSubmit={handleFormSubmit}
          onCancel={() => setState(prev => ({ ...prev, showForm: false, selectedPair: null }))}
        />
      </Modal>

      <ConfirmDialog
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(prev => ({ ...prev, showDeleteConfirm: false, selectedPair: null }))}
        onConfirm={handleDeleteConfirm}
        title="Delete Trading Pair"
        message={`Are you sure you want to delete the trading pair ${state.selectedPair?.symbol}? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-danger-600 hover:bg-danger-700"
      />
    </div>
  );
};

export default TradingPairsPage; 